import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Image as ImageIcon } from "lucide-react";

export function ImagePickerDialog({ open, onOpenChange, images, onInsert }) {
  const navigate = useNavigate();
  const [width, setWidth] = useState("120");

  const handleInsert = (img) => {
    onInsert(img, { width });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-orange-600" strokeWidth={1.75} />
            Recursos gráficos ({images.length})
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Ancho (px)
            </Label>
            <Input type="text" value={width} onChange={(e) => setWidth(e.target.value)}
              className="w-20 h-8 focus-visible:ring-orange-500" placeholder="auto"
              data-testid="image-width-input" />
          </div>
          <div className="text-[11px] text-slate-500 flex-1">
            Dejar vacío para tamaño natural. El HTML insertado incluye <code className="text-orange-700">referrerpolicy="no-referrer"</code> y estilos email-safe.
          </div>
        </div>

        {images.length === 0 ? (
          <div className="py-12 text-center">
            <ImageIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate-500 mb-3">Aún no hay imágenes cargadas.</p>
            <Button onClick={() => { onOpenChange(false); navigate("/images"); }}
              className="bg-orange-600 hover:bg-orange-700 text-white">
              Ir a Imágenes
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto py-2">
            {images.map((img) => (
              <button key={img.id} type="button" onClick={() => handleInsert(img)}
                className="group text-left rounded-md border border-slate-200 bg-white hover:border-orange-400 hover:ring-2 hover:ring-orange-100 transition-all overflow-hidden"
                data-testid={`image-picker-${img.id}`}>
                <div className="bg-slate-50 aspect-video flex items-center justify-center border-b border-slate-200 p-3">
                  <img src={img.url} alt={img.alt_text || img.name}
                    className="max-w-full max-h-full object-contain" referrerPolicy="no-referrer" />
                </div>
                <div className="p-2">
                  <div className="text-xs font-medium text-slate-900 truncate">{img.name}</div>
                  <div className="flex items-center justify-between mt-1">
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-slate-200 text-[9px] uppercase tracking-wider">
                      {img.category}
                    </Badge>
                    <span className="text-[10px] text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                      Insertar →
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline"
            onClick={() => { onOpenChange(false); navigate("/images"); }}
            className="border-slate-200">
            Gestionar imágenes
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-200">
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
